import { Tag } from "antd";

// AUDITORIA-CLINICA-REDISENO (FASE 1) -- render de SOLO LECTURA del contenido
// de cada sección clínica. Es el "Estado 1" de la tarjeta y también lo que se
// vuelve a mostrar tras corregir (Estado 3): una única fuente de verdad para
// "cómo se lee esta sección", en vez de repetir el formato en cada estado.
//
// Todo sale de `detalle.secciones` tal cual lo devuelve la API; nunca se
// deriva ni se completa un dato ausente.

export const SIGNOS_CAMPOS = [
    { id: "presion", label: "Presión" },
    { id: "pulso", label: "Pulso" },
    { id: "temperatura", label: "Temperatura" },
    { id: "saturacion_oxigeno", label: "Saturación O2" },
    { id: "frecuencia_respiratoria", label: "Frec. respiratoria" },
    { id: "peso", label: "Peso" },
    { id: "talla", label: "Talla" },
    { id: "imc", label: "IMC" },
    { id: "perimetro_cefalico", label: "Perímetro cefálico" },
];

const RECETA_ESTADO_TAG = {
    Activa: { color: "green", label: "Vigente" },
    Reemplazada: { color: "orange", label: "Reemplazada" },
    Anulada: { color: "red", label: "Anulada" },
};

const vacio = (texto) => <span className="ac-seccion__vacio">{texto}</span>;
const valorOTexto = (v) => (v === null || v === undefined || v === "" ? "—" : String(v));

const Texto = ({ children }) => <div>{children}</div>;

function CuerpoSignos({ registros }) {
    return (
        <>
            {registros.map((r, i) => (
                <div className="ac-registro" key={r.idsignosvitales ?? i}>
                    {registros.length > 1 && <div className="ac-registro__titulo">Registro #{r.idsignosvitales}</div>}
                    <div className="ac-signos">
                        {SIGNOS_CAMPOS.map((campo) => (
                            <div className="ac-campo" key={campo.id}>
                                <span className="ac-etiqueta">{campo.label}</span>
                                <span>{valorOTexto(r[campo.id])}</span>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </>
    );
}

function CuerpoDiagnosticos({ registros }) {
    return (
        <div className="d-flex flex-column gap-2">
            {registros.map((d) => (
                <div key={d.iddiagnostico_cita_paciente}>
                    <span className="ac-mono">{d.diagnostico?.codigo || "—"}</span>{" "}
                    <span>{d.diagnostico?.nombre || "Diagnóstico sin descripción"}</span>
                    {d.typo_diagnostico && <span className="ac-seccion__vacio"> · {d.typo_diagnostico}</span>}
                    {d.comentario && <div className="ac-seccion__vacio">{d.comentario}</div>}
                </div>
            ))}
        </div>
    );
}

function CuerpoRecetas({ registros }) {
    return (
        <div className="d-flex flex-column gap-2">
            {registros.map((r) => {
                const estado = r.estado || "Activa";
                const tag = RECETA_ESTADO_TAG[estado] || { color: "default", label: estado };
                const sustituta = registros.find((x) => x.reemplaza_a === r.idreceta);
                return (
                    <div key={r.idreceta} className="ac-registro">
                        <div>
                            <Tag color={tag.color}>{tag.label}</Tag>
                            <span>Receta #{r.idreceta}</span>
                        </div>
                        <div>{valorOTexto(r.medicamentos)}</div>
                        <div className="ac-seccion__vacio">{valorOTexto(r.indicaciones)}</div>
                        {sustituta && <div className="ac-seccion__vacio">Sustituida por la receta #{sustituta.idreceta}</div>}
                    </div>
                );
            })}
        </div>
    );
}

function AtencionSeccionCuerpo({ idSeccion, secciones }) {
    const s = secciones || {};
    switch (idSeccion) {
        case "motivo_consulta":
            return <Texto>{s.motivo || vacio("Sin motivo registrado")}</Texto>;
        case "signos_vitales":
            return <CuerpoSignos registros={s.signos_vitales || []} />;
        case "enfermedad_actual":
            return (
                <>
                    {(s.enfermedad_actual || []).map((r) => (
                        <div className="ac-registro" key={r.idenfermedad_actual_cita}>
                            {r.enfermedad_actual || vacio("Sin contenido")}
                        </div>
                    ))}
                </>
            );
        case "diagnosticos":
            return <CuerpoDiagnosticos registros={s.diagnosticos || []} />;
        case "plan_tratamiento":
            return (
                <>
                    {(s.plan_tratamiento || []).map((r) => (
                        <div className="ac-registro" key={r.idplan_tratamiento}>
                            {r.plan_tratamiento || vacio("Sin contenido")}
                        </div>
                    ))}
                </>
            );
        case "nota_evolucion":
            return (
                <>
                    {(s.nota_evolucion || []).map((r) => (
                        <div className="ac-registro" key={r.idnota_evolucion}>
                            {r.nota_evolucion || vacio("Sin contenido")}
                        </div>
                    ))}
                </>
            );
        case "recetas":
            return <CuerpoRecetas registros={s.recetas || []} />;
        default:
            return vacio("Sección no reconocida");
    }
}

export default AtencionSeccionCuerpo;
